import { z } from "zod";

import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

/**
 * BiRefNet Image Background Removal Model
 *
 * Pricing: free model on fal.ai ($0 per compute second)
 * We charge a minimal $0.001 floor to avoid free operations
 *
 * Parameter mappings (simplified for non-technical users):
 * - Quality: Low (1024x1024), High (2048x2048) → operating_resolution
 * - Model: Portrait, General-Fast (Light), General-Premium (Heavy) → model
 *
 * Fixed values (always applied):
 * - output_format: "png" (transparency support)
 * - refine_foreground: true (better edge quality)
 * - output_mask: false (we only need the image output)
 */

const BIREFNET_MODEL_MAP = {
  portrait: "Portrait",
  "general-fast": "General Use (Light)",
  "general-premium": "General Use (Heavy)",
} as const;

const BIREFNET_RESOLUTION_MAP = {
  low: "1024x1024",
  high: "2048x2048",
} as const;

// BiRefNet pricing: $0 per compute second (free model on fal.ai)
// We use a minimal floor of $0.001 (1000 microdollars) to avoid free operations
const BIREFNET_FLOOR_MICRODOLLARS = 1_000;

const birefnetBgRemovalParams = z
  .object({
    bgRemovalModel: z
      .enum(["portrait", "general-fast", "general-premium"])
      .default("general-fast"),
    quality: z.enum(["low", "high"]).default("low"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

type BirefnetBgRemovalParams = z.infer<typeof birefnetBgRemovalParams>;

export const birefnetBgRemoval = defineModel({
  key: "birefnet-bg-removal",
  stepType: "image",
  spec: {
    label: "Remove Background",
    description:
      "Remove backgrounds from images with AI-powered edge refinement. Ideal for product photos, portraits, and graphics.",
    icon: "bg-remove.svg",
    tags: ["image-edit"],
    featured: false,
    features: {
      attachments: true,
      quality: true,
      bgRemovalModel: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/webp", "image/png"],
          label: "Image",
          slot: "input_image",
          group: "input",
          required: true,
        },
      ],
      quality: {
        items: [
          {
            label: "Standard",
            value: "low",
            description: "1024×1024 processing",
          },
          { label: "High", value: "high", description: "2048×2048 processing" },
        ],
      },
      bgRemovalModel: {
        items: [
          {
            label: "Portrait",
            value: "portrait",
            description: "Optimized for people",
          },
          {
            label: "General - Fast",
            value: "general-fast",
            description: "Quick processing for most images",
          },
          {
            label: "General - Premium",
            value: "general-premium",
            description: "Best quality for complex subjects",
          },
        ],
      },
    },
    params: birefnetBgRemovalParams,
    info: "Removes the background from images, leaving only the subject on a transparent background.",
  },
  providers: [
    {
      provider: "fal",
      modelId: "fal-ai/birefnet",
      estimateCost() {
        // Free model on fal.ai — charge a minimal floor to avoid $0 operations
        return BIREFNET_FLOOR_MICRODOLLARS;
      },
      toPayload(params: BirefnetBgRemovalParams) {
        const inputImage = params.attachments?.find(
          (att: { slot: string }) => att.slot === "input_image",
        );

        if (!inputImage?.data) {
          throw new ValidationError(
            "Please provide an image to remove the background from.",
          );
        }

        const modelValue =
          BIREFNET_MODEL_MAP[params.bgRemovalModel ?? "general-fast"];
        const resolutionValue =
          BIREFNET_RESOLUTION_MAP[params.quality ?? "low"];

        return {
          model: this.modelId,
          prompt: "", // BiRefNet doesn't use prompts, but FalImagePayload requires it
          image_url: inputImage.data,
          model_variant: modelValue,
          operating_resolution: resolutionValue,
          output_format: "png",
          refine_foreground: true,
          output_mask: false,
        };
      },
    },
  ],
});
